import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../components/atoms/ScreenSkeleton';
import { CompleteButton } from '../../../components/atoms/Button';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SiteDiggingConditionModel } from '../../../data/types';
import { inspectionDB } from '../../../data/database.structure';
import { SingleLineTextField } from '../../../components/atoms/SingleLineTextField';
import { ScreenHeading } from '../../../components/molecules/ScreenHeading';
import { SectionHeading } from '../../../components/atoms/SectionHeading';
import { ScreenObservationLayout } from '../../../components/templates/ScreenObservationLayout';
import { Label } from '../../../components/atoms/Label';
import { CheckboxWithComponents } from '../../../components/atoms/CheckboxWithComponentsField';
import { DateField } from '../../../components/atoms/DateField';
import DropdownField from '../../../components/atoms/DropdownField';
import { ObservableItemIdentifier } from '../../../api-contracts/other/ObservableItemIdentifier';
import { CHANGEOUT_CHILD_PATHS } from './operatingHoursChangeoutPaths';
import { ErrorBanner } from '../../../components/atoms/ErrorBanner';
import { useAssetMineLocationLevel } from '../../../hooks/useAssetMineLocationLevel';
import useOperatingHours from '../../../hooks/useOperatingHours';
import { toast } from '../../../store/toast';
import {
  EToastType,
  TOAST_DURATIONS,
} from '../../../components/molecules/ToastMessage';
import { observationService } from '../../../services/observationService';
import { observationDraftService } from '../../../services/observationDraftService';
import { ObservationDraftScope } from '../../../components/ObservationDraftScope';
import { useInspectionCached } from '../../../hooks/useInspectionCached';
import { CustomDialogBox } from '../../../components/molecules/CustomDialogBox';
import { DataTypesEnum } from '../../../data/enums';
import {
  cancelPendingNavigationAtom,
  registerNavigationBlockerAtom,
  takePendingNavigationAtom,
  unregisterNavigationBlockerAtom,
} from '../../../store/navigation/navigationBlockerAtom';
import { useSetAtom } from 'jotai';
import { evaluateTotalMachineHours } from '../../../helpers/machineHoursHelpers';

enum UndercarriageChangeOutSide {
  Left = 'Left',
  Right = 'Right',
}

enum OperatingHoursConsts {
  Duration = TOAST_DURATIONS.VALIDATION,
}

// 0 is a valid reading (an undercarriage fitted at commissioning, #9823), so only a
// missing or unparseable value means the change-out has no hours.
const hasChangeoutHours = (value: number | null) =>
  value !== null && !Number.isNaN(value);

export const OperatingHoursScreen = () => {
  const { externalId } = useParams();
  const navigate = useNavigate();

  const [diggingConditions, setDiggingConditions] = useState<
    SiteDiggingConditionModel[]
  >([]);

  const { Operation } = ObservableItemIdentifier.Identifiers;

  const assetMineLocationState = useAssetMineLocationLevel({
    inspectionId: externalId ?? '',
  });

  const registerNavigationBlocker = useSetAtom(registerNavigationBlockerAtom);
  const unregisterNavigationBlocker = useSetAtom(
    unregisterNavigationBlockerAtom,
  );
  const takePendingNavigation = useSetAtom(takePendingNavigationAtom);
  const cancelPendingNavigation = useSetAtom(cancelPendingNavigationAtom);

  useEffect(() => {
    async function getDiggingConditions() {
      try {
        const response = (
          await inspectionDB.siteDiggingConditions.toArray()
        ).sort((a, b) => a.name.localeCompare(b.name));
        setDiggingConditions(response);
      } catch (e) {
        console.error(e);
      }
    }

    getDiggingConditions();
  }, []);

  const [isOpenConfirm, setOpenConfirm] = useState(false);
  // Set by the dialog's GO BACK. Complete raises the dialog with no navigation
  // parked, so GO BACK's own navigate(-1) meets this screen's blocker again, and
  // the change-outs it kept are still incomplete.
  const leavingWithIncompleteChangeoutsRef = useRef(false);

  const [error, setError] = useState<string | null>(null);
  const [inspectionDate, setInspectionDate] = useState<Date | null>(null);
  const [totalMachineHours, setTotalMachineHours] = useState<number | null>(
    null,
  );
  const [leftChangeoutValue, setLeftChangeoutValue] = useState<number | null>(
    null,
  );
  const [leftChangeoutCalculation, setLeftChangeoutCalculation] = useState<
    number | null
  >(null);
  const [rightChangeoutValue, setRightChangeoutValue] = useState<number | null>(
    null,
  );
  const [rightChangeoutCalculation, setRightChangeoutCalculation] = useState<
    number | null
  >(null);
  const [hoursBetweenInspections, setHoursBetweenInspections] = useState<
    string | null
  >();

  const checkLeftRightUndercarriageChangeoutState =
    useCallback(async (): Promise<{
      left: 'good' | 'incomplete';
      right: 'good' | 'incomplete';
    }> => {
      if (externalId) {
        const leftChangeout = await observationService.getObservation({
          dataLevel: assetMineLocationState.observableLevelsEnum,
          entityId: assetMineLocationState.entityId,
          entityExternalId: assetMineLocationState.entityExternalId,
          inspectionCategoryId:
            inspection?.inspection.inspectionCategoryTypes[0].externalId ?? '',
          inspectionId: externalId,
          path: Operation.UndercarriageChangeOut.Left.Checked,
        });
        const leftCheckedChangeout = observationService.convertValue<boolean>(
          leftChangeout?.value ?? '',
          DataTypesEnum.boolean,
        );

        const rightChangeout = await observationService.getObservation({
          dataLevel: assetMineLocationState.observableLevelsEnum,
          entityId: assetMineLocationState.entityId,
          entityExternalId: assetMineLocationState.entityExternalId,
          inspectionCategoryId:
            inspection?.inspection.inspectionCategoryTypes[0].externalId ?? '',
          inspectionId: externalId,
          path: Operation.UndercarriageChangeOut.Right.Checked,
        });
        const rightCheckedChangeout = observationService.convertValue<boolean>(
          rightChangeout?.value ?? '',
          DataTypesEnum.boolean,
        );

        return {
          left:
            leftCheckedChangeout === true &&
            !hasChangeoutHours(leftChangeoutValue)
              ? 'incomplete'
              : 'good',
          right:
            rightCheckedChangeout === true &&
            !hasChangeoutHours(rightChangeoutValue)
              ? 'incomplete'
              : 'good',
        };
      } else {
        return {
          left: 'good',
          right: 'good',
        };
      }
    }, [
      externalId,
      leftChangeoutValue,
      rightChangeoutValue,
      assetMineLocationState,
    ]);

  const checkAbleToNavigateBack = useCallback(async (): Promise<boolean> => {
    if (externalId) {
      const state = await checkLeftRightUndercarriageChangeoutState();

      if (state.left === 'incomplete' || state.right === 'incomplete') {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }, [
    externalId,
    leftChangeoutValue,
    rightChangeoutValue,
    assetMineLocationState,
  ]);

  useEffect(() => {
    // handle back btn clicked for operation hours screen
    const blockerId = 'operating-hours-validation';

    const navigationBlocker = async (): Promise<boolean> => {
      if (leavingWithIncompleteChangeoutsRef.current) {
        return false;
      }
      const canNavigate = await checkAbleToNavigateBack();

      // Same validation logic, but returns true to block or false to allow
      if (externalId) {
        if (!canNavigate) {
          setOpenConfirm(true);
          return true; // Block navigation
        }
      }

      return false; // Allow navigation
    };

    registerNavigationBlocker({
      id: blockerId,
      blocker: navigationBlocker,
    });

    return () => {
      unregisterNavigationBlocker(blockerId);
    };
  }, [
    externalId,
    leftChangeoutValue,
    rightChangeoutValue,
    assetMineLocationState,
  ]);

  const { inspection } = useInspectionCached(externalId ?? '');

  const {
    lastOperatingHoursDataLoading,
    operatingHoursError,
    lastTotalHours,
    lastTotalHoursDate,
    nextTotalHours,
    lastLeftUndercarriageChangeout,
    nextLeftUndercarriageChangeout,
    lastRightUndercarriageChangeout,
    nextRightUndercarriageChangeout,
    lastInspectionMachineHours,
  } = useOperatingHours({
    inspectionId: externalId ?? '',
    assetId: inspection?.asset?.id ?? 0,
    inspectionDate: inspection?.inspection.inspectionDate,
  });

  const formatDateForInfoMessage = (date: Date): string => {
    const dateString = date.toISOString().split('T')[0];
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const lastLeftUndercarriageChangeoutMinDate = useMemo(() => {
    if (lastLeftUndercarriageChangeout) {
      const date = new Date(lastLeftUndercarriageChangeout.changeoutDate);
      // set time to noon to avoid timezone issues
      date.setHours(12, 0, 0, 0);

      // Add one day as you can't select the same day for a new changeout as a previous changeout
      date.setDate(date.getDate() + 1);
      return date;
    }
  }, [lastLeftUndercarriageChangeout]);

  const leftUndercarriageChangeoutInfoMessage = useMemo(() => {
    if (lastLeftUndercarriageChangeout) {
      const formattedDate = formatDateForInfoMessage(
        lastLeftUndercarriageChangeout.changeoutDate,
      );
      return `The last recorded change out date is recorded as ${formattedDate}. Please enter a date after ${formattedDate}.  Note, you cannot enter dates beyond the current inspection date.`;
    }
    return 'You cannot enter dates beyond the current inspection date.';
  }, [lastLeftUndercarriageChangeout]);

  const lastRightUndercarriageChangeoutMinDate = useMemo(() => {
    if (lastRightUndercarriageChangeout) {
      const date = new Date(lastRightUndercarriageChangeout.changeoutDate);
      // set time to noon to avoid timezone issues
      date.setHours(12, 0, 0, 0);

      // Add one day as you can't select the same day for a new changeout as a previous changeout
      date.setDate(date.getDate() + 1);
      return date;
    }
  }, [lastRightUndercarriageChangeout]);

  const rightUndercarriageChangeoutInfoMessage = useMemo(() => {
    if (lastRightUndercarriageChangeout) {
      const formattedDate = formatDateForInfoMessage(
        lastRightUndercarriageChangeout.changeoutDate,
      );
      return `The last recorded change out date is recorded as ${formattedDate}. Please enter a date after ${formattedDate}.  Note, you cannot enter dates beyond the current inspection date.`;
    }
    return 'You cannot enter dates beyond the current inspection date.';
  }, [lastRightUndercarriageChangeout]);

  useEffect(() => {
    async function getAsset() {
      try {
        if (!inspection) {
          throw new Error('Error fetching inspection');
        }

        if (inspectionDate !== inspection.inspection.inspectionDate) {
          setInspectionDate(inspection.inspection.inspectionDate); // only change the value if the value changed
        }

        if (
          lastInspectionMachineHours === undefined ||
          isNaN(lastInspectionMachineHours) ||
          totalMachineHours === null ||
          isNaN(totalMachineHours)
        ) {
          setHoursBetweenInspections('Unknown');
          return;
        } else {
          const value = totalMachineHours - lastInspectionMachineHours;
          setHoursBetweenInspections(Math.max(0, Math.trunc(value)).toString());
        }
      } catch (error) {
        console.error('Error fetching asset', error);
        setError('Error fetching asset');
      }
    }
    if (
      inspection &&
      operatingHoursError === undefined &&
      lastOperatingHoursDataLoading === false
    ) {
      getAsset();
    }
  }, [
    inspection,
    lastOperatingHoursDataLoading,
    operatingHoursError,
    lastTotalHours,
    lastLeftUndercarriageChangeout,
    lastRightUndercarriageChangeout,
    lastInspectionMachineHours,
    totalMachineHours,
  ]);

  useEffect(() => {
    if (leftChangeoutValue !== null) {
      const subtraction = totalMachineHours! - leftChangeoutValue;
      setLeftChangeoutCalculation(
        Number.isNaN(subtraction) ? null : subtraction,
      );
    }
    if (rightChangeoutValue !== null) {
      const subtraction = totalMachineHours! - rightChangeoutValue;
      setRightChangeoutCalculation(
        Number.isNaN(subtraction) ? null : subtraction,
      );
    }
  }, [totalMachineHours, leftChangeoutValue, rightChangeoutValue]);

  function validateChangeoutValue({
    value,
    side,
    total = totalMachineHours,
  }: {
    value: number;
    side: UndercarriageChangeOutSide;
    /** Total machine hours to check against, when state does not hold it yet. */
    total?: number | null;
  }): boolean {
    // 0 is valid: it records an undercarriage fitted at machine commissioning (issue #9823),
    // matching the backend validator which now accepts 0.
    if (value < 0) {
      return false;
    }

    if (total === null || isNaN(total)) {
      toast.add({
        message: `Total machine hours is not set`,
        type: EToastType.alert,
        isOpen: true,
        options: {
          duration: OperatingHoursConsts.Duration,
        },
      });
      return false;
    }

    if (side === UndercarriageChangeOutSide.Left) {
      if (lastLeftUndercarriageChangeout !== undefined) {
        if (value < lastLeftUndercarriageChangeout.changeoutHours) {
          toast.add({
            message: `Left Undercarriage Changeout cannot be less than ${lastLeftUndercarriageChangeout.changeoutHours}`,
            type: EToastType.alert,
            isOpen: true,
            options: {
              duration: OperatingHoursConsts.Duration,
            },
          });
          return false;
        }
      }

      if (nextLeftUndercarriageChangeout !== undefined) {
        if (value > nextLeftUndercarriageChangeout.changeoutHours) {
          toast.add({
            message: `Left Undercarriage Changeout cannot be greater than ${nextLeftUndercarriageChangeout.changeoutHours}`,
            type: EToastType.alert,
            isOpen: true,
            options: {
              duration: OperatingHoursConsts.Duration,
            },
          });
          return false;
        }
      }
    } else if (side === UndercarriageChangeOutSide.Right) {
      if (lastRightUndercarriageChangeout !== undefined) {
        if (value < lastRightUndercarriageChangeout.changeoutHours) {
          toast.add({
            message: `Right Undercarriage Changeout cannot be less than ${lastRightUndercarriageChangeout.changeoutHours}`,
            type: EToastType.alert,
            isOpen: true,
            options: {
              duration: OperatingHoursConsts.Duration,
            },
          });
          return false;
        }
      }

      if (nextRightUndercarriageChangeout !== undefined) {
        if (value > nextRightUndercarriageChangeout.changeoutHours) {
          toast.add({
            message: `Right Undercarriage Changeout cannot be greater than ${nextRightUndercarriageChangeout.changeoutHours}`,
            type: EToastType.alert,
            isOpen: true,
            options: {
              duration: OperatingHoursConsts.Duration,
            },
          });
          return false;
        }
      }
    }

    const numberValue = value;

    if (isNaN(numberValue)) {
      return false;
    }

    const subtraction = total - numberValue;

    if (subtraction < 0) {
      toast.add({
        message: `Undercarriage Changeout cannot be greater than ${total}`,
        type: EToastType.alert,
        isOpen: true,
        options: {
          duration: OperatingHoursConsts.Duration,
        },
      });
      return false;
    }

    return true;
  }

  if (
    assetMineLocationState.isLoading ||
    externalId === undefined ||
    lastOperatingHoursDataLoading
  ) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  const handleCheckChangeout = async (
    value: boolean,
    position: 'left' | 'right',
  ) => {
    if (value === false) {
      position === 'left'
        ? setLeftChangeoutCalculation(null)
        : setRightChangeoutCalculation(null);
      position === 'left'
        ? setLeftChangeoutValue(null)
        : setRightChangeoutValue(null);

      const sideKey = position === 'left' ? 'Left' : 'Right';
      const changeoutPaths = Operation.UndercarriageChangeOut[sideKey];
      const pathsToDelete = [
        changeoutPaths.Checked,
        changeoutPaths.Date,
        changeoutPaths.MachineHoursAtChange,
      ];

      const baseDeleteProps = {
        dataLevel: assetMineLocationState.observableLevelsEnum,
        entityId: assetMineLocationState.entityId,
        entityExternalId: assetMineLocationState.entityExternalId,
        inspectionCategoryId:
          inspection?.inspection.inspectionCategoryTypes[0].externalId ?? '',
        inspectionId: externalId,
      };

      // Draft-aware clear: tombstone via the draft service so these deletes WIN (last write)
      // over the checkbox atom's setValue('false') and any pending Date/MachineHoursAtChange
      // atom drafts - which would otherwise re-create the cleared change-out on the next flush
      // once the screen is wrapped. Also correct when unwrapped (the tombstone flush deletes
      // any legacy-written rows for these paths).
      for (const path of pathsToDelete) {
        observationDraftService.markDeleted({ ...baseDeleteProps, path });
      }
    }
  };

  // Meter integrity: propel-type hours are a subset of machine hours. The SQL
  // audit found a synced inspection with propel 30,000 > machine 29,000 -
  // nothing guarded these fields (machine hours had its own last/next guard).
  const validatePropelWithinMachineHours = (value: string): boolean => {
    const propel = parseInt(value);
    if (
      totalMachineHours !== null &&
      !Number.isNaN(totalMachineHours) &&
      propel > totalMachineHours
    ) {
      toast.add({
        message: `Propel hours cannot exceed total machine hours (${totalMachineHours})`,
        type: EToastType.alert,
        isOpen: true,
        options: { duration: OperatingHoursConsts.Duration },
      });
      return false;
    }
    return true;
  };

  return (
    <ObservationDraftScope inspectionId={externalId}>
      <div>
        <div className="sticky -top-4 z-10 bg-white">
          <ScreenHeading
            eyebrow={'Machine'}
            title={'Operation'}
            tooltipKey={'operating-hours-screen-header'}
          />
          <div className="-mx-4 flex items-center justify-center border-b border-line px-4 py-3 font-bold text-ink">
            Machine hours between inspections: {hoursBetweenInspections}{' '}
            {hoursBetweenInspections && hoursBetweenInspections !== 'Unknown'
              ? parseInt(hoursBetweenInspections) > 1
                ? 'Hours'
                : 'Hour'
              : ''}
          </div>
        </div>

        <CustomDialogBox
          title="Undercarriage Changeout Hours"
          enabled={true}
          isOpen={isOpenConfirm}
          description="Some Undercarriage Changeouts do not have Hours entered. 
        Would you like to continue editing so that the Hours can be entered, or you can Go Back"
          leftButtonText="GO BACK"
          rightButtonText="CONTINUE EDITING"
          onLeftClick={() => {
            // Claim the parked navigation up front: the dialog reports its own
            // dismissal straight after this handler runs, and that dismissal
            // would otherwise cancel the resume.
            const resume = takePendingNavigation();

            // Leave the change-outs as recorded. Deleting the incomplete ones here
            // also threw away a Checked, Date and Hours the inspector had entered;
            // a change-out without hours still syncs, with no hours.
            leavingWithIncompleteChangeoutsRef.current = true;

            // Land on wherever the inspector was actually heading — a Drawer
            // link goes to that screen, not back one step. Complete raises this
            // dialog itself without attempting a navigation, so there is
            // nothing parked on that path and going back one entry is right.
            if (resume) {
              resume.proceed();
              return;
            }
            navigate(-1);
          }}
          onBackdropClicked={() => {
            setOpenConfirm(false);
            cancelPendingNavigation();
          }}
          onRightClick={() => {}}
          trigger={<div></div>}
        />

        <ScreenObservationLayout className="mt-4">
          <SectionHeading title="Pit conditions" />
          <DropdownField
            title="What are the digging conditions today"
            titleType="body"
            tooltipKey={
              'operating-hours-undercarriage-digging-conditions-today'
            }
            placeholder=""
            options={diggingConditions.map((el) => el.name)}
            path={Operation.PitCondition.CurrentDiggingConditions}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            dataLevel={assetMineLocationState.observableLevelsEnum}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
          />
          <DropdownField
            title="Digging since last inspection"
            titleType="body"
            tooltipKey={
              'operating-hours-undercarriage-digging-since-last-inspection'
            }
            placeholder=""
            options={diggingConditions.map((el) => el.name)}
            path={Operation.PitCondition.DiggingConditionsSinceLastInspection}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            dataLevel={assetMineLocationState.observableLevelsEnum}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
          />
          <SingleLineTextField
            title="Total tonnage"
            tooltipKey={'operating-hours-undercarriage-total-tonnage'}
            placeholder=""
            type="integer"
            path={Operation.PitCondition.TotalTonnage}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            dataLevel={assetMineLocationState.observableLevelsEnum}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
          />
          <SingleLineTextField
            title="Total bank cubic meters (BCM)"
            tooltipKey={'operating-hours-undercarriage-total-bank-cubic-meters'}
            placeholder=""
            type="integer"
            path={Operation.PitCondition.TotalBankCubicMeters}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            dataLevel={assetMineLocationState.observableLevelsEnum}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
          />
          <SectionHeading title="Machine" />
          <SingleLineTextField
            title="Total machine hours"
            type="integer"
            dataLevel={assetMineLocationState.observableLevelsEnum}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
            path={Operation.TotalMachineHours}
            onChange={(value) => {
              const machineHours = parseInt(value);
              setTotalMachineHours(machineHours);

              // Re-check each recorded changeout against the reading just entered
              // (state still holds the previous one). One that no longer fits is
              // reported, but its recorded hours stay, on both sides.
              if (leftChangeoutValue !== null) {
                const valid =
                  !Number.isNaN(machineHours) &&
                  validateChangeoutValue({
                    value: leftChangeoutValue,
                    side: UndercarriageChangeOutSide.Left,
                    total: machineHours,
                  });
                if (!valid) {
                  setLeftChangeoutCalculation(null);
                } else {
                  const subtraction = machineHours - leftChangeoutValue;
                  setLeftChangeoutCalculation(
                    Number.isNaN(subtraction) ? null : subtraction,
                  );
                }
              }
              if (rightChangeoutValue !== null) {
                const valid =
                  !Number.isNaN(machineHours) &&
                  validateChangeoutValue({
                    value: rightChangeoutValue,
                    side: UndercarriageChangeOutSide.Right,
                    total: machineHours,
                  });
                if (!valid) {
                  setRightChangeoutCalculation(null);
                } else {
                  const subtraction = machineHours - rightChangeoutValue;
                  setRightChangeoutCalculation(
                    Number.isNaN(subtraction) ? null : subtraction,
                  );
                }
              }
            }}
            onDefaultLoad={(value) => {
              setTotalMachineHours(parseInt(value));
            }}
            tooltipKey={'total-machine-hours'}
            valdiation={(value) => {
              // Mirrors the backend's #9823/#9824 rules (backwards meter + >24 h/day
              // plausibility) so the inspector is told at entry time, not at submit (#10549).
              const result = evaluateTotalMachineHours({
                value: parseInt(value),
                inspectionDate: inspection?.inspection.inspectionDate,
                lastTotalHours,
                lastTotalHoursDate,
                nextTotalHours,
              });

              if (!result.isValid && result.message) {
                toast.add({
                  message: result.message,
                  type: EToastType.alert,
                  isOpen: true,
                  options: {
                    duration: OperatingHoursConsts.Duration,
                  },
                });
              }

              return result.isValid;
            }}
          />

          <SingleLineTextField
            title="Total propel hours"
            type="integer"
            dataLevel={assetMineLocationState.observableLevelsEnum}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
            path={Operation.TotalPropelHours}
            tooltipKey={'total-propel-hours'}
            valdiation={validatePropelWithinMachineHours}
          />

          <Label
            titleType="largeHeader"
            tooltipKey={'operating-hours-undercarriage-propel-hours-heading'}
          >
            Undercarriage propel hours
          </Label>
          <Label
            titleType="fieldTitle"
            children="Left"
            className="text-primaryColor"
          />
          <SingleLineTextField
            type="integer"
            dataLevel={assetMineLocationState.observableLevelsEnum}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
            path={Operation.UndercarriagePropelHours.Left}
            valdiation={validatePropelWithinMachineHours}
          />

          <Label
            titleType="fieldTitle"
            children="Right"
            className="text-primaryColor"
          />
          <SingleLineTextField
            type="integer"
            dataLevel={assetMineLocationState.observableLevelsEnum}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
            path={Operation.UndercarriagePropelHours.Right}
            valdiation={validatePropelWithinMachineHours}
          />

          <Label
            titleType="largeHeader"
            children="Changeout"
            className="my-4 text-primaryColor"
            tooltipKey={'operating-hours-undercarriage-changeout-heading'}
          />

          <CheckboxWithComponents
            childPaths={[...CHANGEOUT_CHILD_PATHS.left]}
            label="Changeout (Left)"
            dataLevel={assetMineLocationState.observableLevelsEnum}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
            path={Operation.UndercarriageChangeOut.Left.Checked}
            onChange={async (value) =>
              await handleCheckChangeout(value, 'left')
            }
          >
            <ScreenObservationLayout>
              <DateField
                maxDate={inspection?.inspection.inspectionDate}
                maxDateErrorMessage="Date cannot be after the current Inspection Date"
                minDate={lastLeftUndercarriageChangeoutMinDate}
                minDateErrorMessage="Date cannot be the same or before last Left Undercarriage changeout"
                dataLevel={assetMineLocationState.observableLevelsEnum}
                entityId={assetMineLocationState.entityId}
                entityExternalId={assetMineLocationState.entityExternalId}
                inspectionId={externalId}
                inspectionCategoryTypeId={
                  inspection?.inspection.inspectionCategoryTypes[0]
                    .externalId ?? ''
                }
                path={
                  ObservableItemIdentifier.Identifiers.Operation
                    .UndercarriageChangeOut.Left.Date
                }
                infoText={leftUndercarriageChangeoutInfoMessage}
              />

              <SingleLineTextField
                title="Machine Hours at Changeout"
                type="integer"
                dataLevel={assetMineLocationState.observableLevelsEnum}
                entityId={assetMineLocationState.entityId}
                entityExternalId={assetMineLocationState.entityExternalId}
                inspectionId={externalId}
                inspectionCategoryTypeId={
                  inspection?.inspection.inspectionCategoryTypes[0]
                    .externalId ?? ''
                }
                path={
                  Operation.UndercarriageChangeOut.Left.MachineHoursAtChange
                }
                valdiation={(value) => {
                  const valid = validateChangeoutValue({
                    value: parseFloat(value),
                    side: UndercarriageChangeOutSide.Left,
                  });

                  return valid;
                }}
                onChange={(value) => {
                  if (totalMachineHours === null) {
                    setError('Total machine hours is not set');
                    return;
                  }

                  const numberValue = parseFloat(value);
                  const subtraction = totalMachineHours! - numberValue;
                  setLeftChangeoutCalculation(
                    Number.isNaN(subtraction) ? null : subtraction,
                  );
                  setLeftChangeoutValue(numberValue);
                }}
                onDefaultLoad={(value) => {
                  if (totalMachineHours === null) {
                    return;
                  }
                  const numberValue = parseFloat(value);
                  const subtraction = totalMachineHours! - numberValue;
                  setLeftChangeoutCalculation(
                    Number.isNaN(subtraction) ? null : subtraction,
                  );
                  setLeftChangeoutValue(numberValue);
                }}
              />
            </ScreenObservationLayout>
          </CheckboxWithComponents>

          <CheckboxWithComponents
            childPaths={[...CHANGEOUT_CHILD_PATHS.right]}
            label="Changeout (Right)"
            dataLevel={assetMineLocationState.observableLevelsEnum}
            entityId={assetMineLocationState.entityId}
            entityExternalId={assetMineLocationState.entityExternalId}
            inspectionId={externalId}
            inspectionCategoryTypeId={
              inspection?.inspection.inspectionCategoryTypes[0].externalId ?? ''
            }
            path={Operation.UndercarriageChangeOut.Right.Checked}
            onChange={async (value) =>
              await handleCheckChangeout(value, 'right')
            }
          >
            <ScreenObservationLayout>
              <DateField
                maxDate={inspection?.inspection.inspectionDate}
                maxDateErrorMessage="Date cannot be after the current Inspection Date"
                minDate={lastRightUndercarriageChangeoutMinDate}
                minDateErrorMessage="Date cannot be the same or before last Right Undercarriage changeout"
                dataLevel={assetMineLocationState.observableLevelsEnum}
                entityId={assetMineLocationState.entityId}
                entityExternalId={assetMineLocationState.entityExternalId}
                inspectionId={externalId}
                inspectionCategoryTypeId={
                  inspection?.inspection.inspectionCategoryTypes[0]
                    .externalId ?? ''
                }
                path={
                  ObservableItemIdentifier.Identifiers.Operation
                    .UndercarriageChangeOut.Right.Date
                }
                infoText={rightUndercarriageChangeoutInfoMessage}
              />

              <SingleLineTextField
                title="Machine Hours at Changeout"
                type="integer"
                dataLevel={assetMineLocationState.observableLevelsEnum}
                entityId={assetMineLocationState.entityId}
                entityExternalId={assetMineLocationState.entityExternalId}
                inspectionId={externalId}
                inspectionCategoryTypeId={
                  inspection?.inspection.inspectionCategoryTypes[0]
                    .externalId ?? ''
                }
                path={
                  Operation.UndercarriageChangeOut.Right.MachineHoursAtChange
                }
                valdiation={(value) =>
                  validateChangeoutValue({
                    value: parseFloat(value),
                    side: UndercarriageChangeOutSide.Right,
                  })
                }
                onChange={(value) => {
                  if (totalMachineHours === null) {
                    setError('Total machine hours is not set');
                    return;
                  }

                  const numberValue = parseFloat(value);
                  const subtraction = totalMachineHours! - numberValue;
                  setRightChangeoutCalculation(
                    Number.isNaN(subtraction) ? null : subtraction,
                  );
                  setRightChangeoutValue(numberValue);
                }}
                onDefaultLoad={(value) => {
                  if (totalMachineHours === null) {
                    return;
                  }
                  const numberValue = parseFloat(value);
                  const subtraction = totalMachineHours! - numberValue;
                  setRightChangeoutCalculation(
                    Number.isNaN(subtraction) ? null : subtraction,
                  );
                  setRightChangeoutValue(numberValue);
                }}
              />
            </ScreenObservationLayout>
          </CheckboxWithComponents>

          <Label titleType="fieldTitle" className="my-4 text-primaryColor">
            Left Undercarriage Hours
          </Label>
          <ScreenObservationLayout>
            <div className="font-bold">
              {leftChangeoutCalculation ?? 'Enter machine hours...'}
            </div>
          </ScreenObservationLayout>

          <Label titleType="fieldTitle" className="my-4 text-primaryColor">
            Right Undercarriage Hours
          </Label>
          <ScreenObservationLayout>
            <div className="font-bold">
              {rightChangeoutCalculation ?? 'Enter machine hours...'}
            </div>
          </ScreenObservationLayout>

          <CompleteButton
            onClick={async () => {
              const canNavigate = await checkAbleToNavigateBack();

              if (!canNavigate) {
                setOpenConfirm(true);
                return;
              }

              await observationService.upsertObservation({
                inspectionId: externalId ?? '',
                inspectionCategoryId:
                  inspection?.inspection.inspectionCategoryTypes[0]
                    .externalId ?? '',
                dataLevel: assetMineLocationState.observableLevelsEnum,
                entityId: assetMineLocationState.entityId,
                entityExternalId: assetMineLocationState.entityExternalId,
                path: ObservableItemIdentifier.Identifiers.Operation.Completed,
                value: new Date().toISOString(),
              });

              // Flush drafts BEFORE navigating: a cross-document back (deep link,
              // post-reload history) aborts the unmount/pagehide flush mid-transaction
              // and silently drops still-dirty field values.
              await observationDraftService.flush(externalId ?? '');
              navigate(-1);
            }}
          />
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
